import {
	getFormProps,
	getInputProps,
	getSelectProps,
	getTextareaProps,
	type FieldMetadata,
	type SubmissionResult,
	useForm,
	useInputControl,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	lazy,
	Suspense,
	type KeyboardEvent,
	useEffect,
	useId,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react'
import { Form, Link } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import { cn, useIsPending } from '#app/utils/misc.tsx'
import {
	SalesLeadSchema,
	salesLeadBusinessTypes,
	type SalesLead,
} from '#app/utils/sales-lead-validation.ts'

const LazyCal = lazy(() => import('@calcom/embed-react'))
const directScheduleLink = 'dealerproedge/30min'
const subscribeToHydration = () => () => {}

type BookDemoTabbedFormProps = {
	isSubmitted?: boolean
	lastResult?: SubmissionResult<string[]> | null
	scheduleNamespace: string
	successDescription?: string
	usePairedFieldRows?: boolean
}

export function BookDemoTabbedForm({
	isSubmitted = false,
	lastResult,
	scheduleNamespace,
	successDescription = 'Your details went to the DealerProEdge team. We will review your website and business type before reaching out.',
	usePairedFieldRows = false,
}: BookDemoTabbedFormProps) {
	const isPending = useIsPending()
	const [activeTab, setActiveTab] = useState('message')
	const [calReady, setCalReady] = useState(false)
	const [bookingComplete, setBookingComplete] = useState(false)
	const [form, fields] = useForm({
		id: 'sales-lead-form',
		constraint: getZodConstraint(SalesLeadSchema),
		lastResult,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SalesLeadSchema })
		},
		shouldValidate: 'onBlur',
		shouldRevalidate: 'onInput',
	})

	useEffect(() => {
		if (activeTab !== 'schedule' || bookingComplete) return

		let mounted = true
		setCalReady(false)

		void (async function () {
			try {
				const { getCalApi } = await import('@calcom/embed-react')
				const cal = await getCalApi({ namespace: scheduleNamespace })

				if (!mounted) return

				cal('ui', {
					hideEventTypeDetails: false,
					layout: 'month_view',
				})

				cal('on', {
					action: 'bookingSuccessful',
					callback: () => {
						if (mounted) {
							setBookingComplete(true)
						}
					},
				})

				setCalReady(true)
			} catch (error) {
				console.error('Error initializing Cal.com:', error)
			}
		})()

		return () => {
			mounted = false
		}
	}, [activeTab, bookingComplete, scheduleNamespace])

	return (
		<Tabs
			value={activeTab}
			onValueChange={(value) => setActiveTab(value)}
			className="mx-auto w-full max-w-[560px]"
		>
			<TabsList className="grid h-auto w-full grid-cols-2 rounded-none border border-black bg-[#f7f7f2] p-0 text-black">
				<TabsTrigger
					value="message"
					className="text-black/58 h-12 rounded-none border-r border-black bg-[repeating-linear-gradient(135deg,rgba(16,16,16,0.045)_0,rgba(16,16,16,0.045)_1px,transparent_1px,transparent_8px)] px-3 text-sm font-medium shadow-none transition-colors hover:bg-[#fffffa] hover:text-black focus-visible:ring-[#EC1C23]/40 data-[state=active]:bg-[#fffffa] data-[state=active]:text-black data-[state=active]:shadow-none md:h-16 md:text-base"
				>
					Send a message
				</TabsTrigger>
				<TabsTrigger
					value="schedule"
					className="text-black/58 h-12 rounded-none bg-[repeating-linear-gradient(135deg,rgba(16,16,16,0.045)_0,rgba(16,16,16,0.045)_1px,transparent_1px,transparent_8px)] px-3 text-sm font-medium shadow-none transition-colors hover:bg-[#fffffa] hover:text-black focus-visible:ring-[#EC1C23]/40 data-[state=active]:bg-[#fffffa] data-[state=active]:text-black data-[state=active]:shadow-none md:h-16 md:text-base"
				>
					Schedule directly
				</TabsTrigger>
			</TabsList>

			<div className="relative border border-black/10 bg-[#fffffa] shadow-[0_30px_90px_-70px_rgba(16,16,16,0.45)]">
				<div className="pointer-events-none absolute -left-3 -top-3 h-6 w-6 border-l border-t border-black/40" />
				<div className="pointer-events-none absolute -bottom-3 -right-3 h-6 w-6 border-b border-r border-black/40" />

				<TabsContent value="message" className="m-0 outline-none">
					{isSubmitted ? (
						<div className="p-8 md:p-10">
							<p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#EC1C23]">
								Submitted
							</p>
							<h2 className="mt-6 text-3xl font-bold md:text-4xl">
								Thanks. We'll be in touch shortly.
							</h2>
							<p className="text-black/66 mt-4 max-w-xl text-base leading-7">
								{successDescription}
							</p>
							<Link
								to="/"
								className="mt-8 inline-flex items-center gap-2 border border-black/15 px-5 py-3 text-sm font-semibold text-black transition-colors hover:border-[#EC1C23] hover:text-[#EC1C23]"
							>
								Back to home
							</Link>
						</div>
					) : (
						<Form method="POST" preventScrollReset {...getFormProps(form)}>
							<HoneypotInputs />
							{usePairedFieldRows ? (
								<>
									<div className="grid grid-cols-1 border-t border-black/10 sm:grid-cols-2">
										<FloatingField
											label="First Name"
											field={fields.firstName}
											autoComplete="given-name"
											className="border-t-0"
										/>
										<FloatingField
											label="Last Name"
											field={fields.lastName}
											autoComplete="family-name"
											className="border-t border-black/10 sm:border-l sm:border-t-0"
										/>
									</div>
									<div className="grid grid-cols-1 border-t border-black/10 sm:grid-cols-2">
										<FloatingField
											label="Phone Number"
											field={fields.phone}
											type="tel"
											autoComplete="tel"
											className="border-t-0"
										/>
										<FloatingField
											label="Email Address"
											field={fields.email}
											type="email"
											autoComplete="email"
											className="border-t border-black/10 sm:border-l sm:border-t-0"
										/>
									</div>
									<div className="grid grid-cols-1 border-t border-black/10 sm:grid-cols-2">
										<FloatingField
											label="Job Title"
											field={fields.jobTitle}
											autoComplete="organization-title"
											className="border-t-0"
										/>
										<FloatingField
											label="Company Name"
											field={fields.dealership}
											autoComplete="organization"
											className="border-t border-black/10 sm:border-l sm:border-t-0"
										/>
									</div>
								</>
							) : (
								<>
									<FloatingField
										label="First Name"
										field={fields.firstName}
										autoComplete="given-name"
									/>
									<FloatingField
										label="Last Name"
										field={fields.lastName}
										autoComplete="family-name"
									/>
									<FloatingField
										label="Phone Number"
										field={fields.phone}
										type="tel"
										autoComplete="tel"
									/>
									<FloatingField
										label="Email Address"
										field={fields.email}
										type="email"
										autoComplete="email"
									/>
									<FloatingField
										label="Job Title"
										field={fields.jobTitle}
										autoComplete="organization-title"
									/>
									<FloatingField
										label="Company Name"
										field={fields.dealership}
										autoComplete="organization"
									/>
								</>
							)}
							<FloatingSelect
								label="Business Type"
								field={fields.dealerType}
								options={salesLeadBusinessTypes}
							/>
							<FloatingField
								label="Current Website"
								field={fields.website}
								type="text"
								autoComplete="url"
							/>
							<FloatingTextarea
								label="Share any relevant project details, requirements, and timeline."
								field={fields.message}
							/>

							<div className="space-y-4 border-t border-black/10 p-5 md:p-6">
								<p className="text-black/48 text-xs leading-5">
									By submitting this form, you agree to our{' '}
									<Link
										to="/website-terms-of-use"
										className="underline decoration-black/25 underline-offset-4 transition-colors hover:text-black hover:decoration-black"
									>
										Terms of Use
									</Link>{' '}
									and{' '}
									<Link
										to="/website-privacy-policy"
										className="underline decoration-black/25 underline-offset-4 transition-colors hover:text-black hover:decoration-black"
									>
										Privacy Notice
									</Link>
									. We will use your contact information to respond to your
									request.
								</p>

								<FieldErrors
									id={form.errorId}
									errors={form.errors}
									className="text-sm font-medium text-[#EC1C23]"
									role="alert"
								/>
							</div>

							<StatusButton
								type="submit"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								disabled={isPending}
								className="!flex h-auto w-full !justify-start rounded-none border-0 border-t border-black/10 bg-transparent px-6 py-7 text-lg font-normal text-black transition-colors hover:bg-black hover:text-white md:py-8 [&>div:first-child]:flex [&>div:first-child]:w-full [&>div:first-child]:items-center [&>div:first-child]:justify-between"
							>
								<span className="flex w-full items-center justify-between">
									<span>Submit</span>
									<ArrowRightIcon className="h-5 w-5" />
								</span>
							</StatusButton>
						</Form>
					)}
				</TabsContent>

				<TabsContent value="schedule" className="m-0 outline-none">
					<DirectSchedulePanel
						bookingComplete={bookingComplete}
						calReady={calReady}
						scheduleNamespace={scheduleNamespace}
					/>
				</TabsContent>
			</div>
		</Tabs>
	)
}

function DirectSchedulePanel({
	bookingComplete,
	calReady,
	scheduleNamespace,
}: {
	bookingComplete: boolean
	calReady: boolean
	scheduleNamespace: string
}) {
	if (bookingComplete) {
		return (
			<div className="p-8 md:p-10">
				<p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#EC1C23]">
					Scheduled
				</p>
				<h2 className="mt-6 text-3xl font-bold md:text-4xl">You're all set.</h2>
				<p className="text-black/66 mt-4 max-w-xl text-base leading-7">
					We look forward to meeting with you soon. You'll receive a calendar
					invite with the details.
				</p>
			</div>
		)
	}

	return (
		<div className="p-5 md:p-6">
			<div className="border-b border-black/10 pb-5">
				<p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#EC1C23]">
					Direct booking
				</p>
				<h2 className="mt-4 text-2xl font-medium leading-tight md:text-3xl">
					Schedule your call.
				</h2>
				<p className="text-black/58 mt-3 text-sm leading-6">
					Pick a time that works best for you and we will walk through the right
					next steps for your dealership.
				</p>
			</div>

			<div className="relative min-h-[520px]">
				{!calReady ? (
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="flex flex-col items-center gap-3">
							<div className="h-8 w-8 animate-spin rounded-full border border-black/15 border-t-[#EC1C23]" />
							<p className="text-sm text-black/50">Loading calendar...</p>
						</div>
					</div>
				) : null}
				<div className={cn(!calReady && 'invisible h-0 overflow-hidden')}>
					<Suspense fallback={null}>
						<LazyCal
							namespace={scheduleNamespace}
							calLink={directScheduleLink}
							style={{ width: '100%', minHeight: '520px' }}
							config={{
								layout: 'month_view',
								theme: 'light',
							}}
						/>
					</Suspense>
				</div>
			</div>
		</div>
	)
}

function FloatingField({
	label,
	field,
	type = 'text',
	autoComplete,
	className,
}: {
	label: string
	field: FieldMetadata<string, SalesLead>
	type?: 'email' | 'tel' | 'text' | 'url'
	autoComplete?: string
	className?: string
}) {
	return (
		<div className={cn('border-t border-black/10', className)}>
			<div className="group relative min-h-[72px]">
				<span className="pointer-events-none absolute left-0 top-0 h-full w-[2px] bg-black opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
				<input
					{...getInputProps(field, { type })}
					placeholder=" "
					autoCapitalize={type === 'email' || type === 'tel' ? 'none' : 'words'}
					autoComplete={autoComplete}
					autoCorrect="off"
					className="peer absolute inset-0 h-full w-full bg-transparent px-5 pb-3 pt-7 text-base text-black outline-none md:px-6"
				/>
				<label
					htmlFor={field.id}
					className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-base font-normal text-black/60 transition-[font-size,top,transform] duration-200 ease-out peer-focus:top-4 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-black/60 peer-[:not(:placeholder-shown)]:top-4 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs md:left-6"
				>
					{label}
					{field.required ? <span className="text-[#EC1C23]"> *</span> : null}
				</label>
			</div>
			<FieldErrors
				id={field.errorId}
				errors={field.errors}
				className="px-5 pb-3 text-xs font-medium text-[#EC1C23] md:px-6"
			/>
		</div>
	)
}

function FloatingSelect({
	label,
	field,
	options,
}: {
	label: string
	field: FieldMetadata<string, SalesLead>
	options: ReadonlyArray<string>
}) {
	const fieldId = useId()
	const buttonRef = useRef<HTMLButtonElement>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const control = useInputControl(field)
	const isHydrated = useSyncExternalStore(
		subscribeToHydration,
		() => true,
		() => false,
	)
	const [isOpen, setIsOpen] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)
	const selectedValue = control.value ?? ''
	const activeOptionId = `${fieldId}-option-${activeIndex}`
	const listboxId = `${fieldId}-listbox`
	const selectedLabel = selectedValue || 'Select...'

	useEffect(() => {
		if (!isOpen) return

		function handlePointerDown(event: PointerEvent) {
			if (!wrapperRef.current?.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}

		document.addEventListener('pointerdown', handlePointerDown)
		return () => document.removeEventListener('pointerdown', handlePointerDown)
	}, [isOpen])

	if (!isHydrated) {
		return (
			<div className="border-t border-black/10">
				<div className="group relative min-h-[88px]">
					<span className="pointer-events-none absolute left-0 top-0 h-full w-[2px] bg-black opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
					<label
						htmlFor={field.id}
						className="pointer-events-none absolute left-5 top-4 text-xs font-normal text-black/60 md:left-6"
					>
						{label}
						{field.required ? <span className="text-[#EC1C23]"> *</span> : null}
					</label>
					<select
						{...getSelectProps(field)}
						className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent px-5 pb-3 pr-12 pt-9 text-base text-black outline-none md:px-6 md:pr-12"
					>
						<option value="">Select...</option>
						{options.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
					<ChevronDownIcon className="pointer-events-none absolute right-5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/60 md:right-6" />
				</div>
				<FieldErrors
					id={field.errorId}
					errors={field.errors}
					className="px-5 pb-3 text-xs font-medium text-[#EC1C23] md:px-6"
				/>
			</div>
		)
	}

	function openMenu(nextIndex = activeIndex) {
		setActiveIndex(nextIndex)
		setIsOpen(true)
	}

	function selectOption(option: string) {
		control.change(option)
		setIsOpen(false)
		window.requestAnimationFrame(() => buttonRef.current?.focus())
	}

	function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
		if (!isOpen) {
			if (
				event.key === 'ArrowDown' ||
				event.key === 'ArrowUp' ||
				event.key === 'Enter' ||
				event.key === ' '
			) {
				event.preventDefault()
				const selectedIndex = options.findIndex(
					(option) => option === selectedValue,
				)
				openMenu(selectedIndex >= 0 ? selectedIndex : 0)
			}
			return
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault()
			setActiveIndex((currentIndex) => (currentIndex + 1) % options.length)
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault()
			setActiveIndex(
				(currentIndex) => (currentIndex - 1 + options.length) % options.length,
			)
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			selectOption(options[activeIndex] ?? options[0] ?? '')
		}

		if (event.key === 'Escape' || event.key === 'Tab') {
			setIsOpen(false)
		}
	}

	return (
		<div ref={wrapperRef} className="border-t border-black/10">
			<div className="group relative min-h-[88px] overflow-visible">
				<span
					className={`pointer-events-none absolute left-0 top-0 h-full w-[2px] bg-black transition-opacity duration-150 ${
						isOpen ? 'opacity-100' : 'opacity-0 group-focus-within:opacity-100'
					}`}
				/>
				<label
					htmlFor={field.id}
					className="pointer-events-none absolute left-5 top-4 text-xs font-normal text-black/60 md:left-6"
				>
					{label}
					{field.required ? <span className="text-[#EC1C23]"> *</span> : null}
				</label>
				<input
					key={field.key}
					name={field.name}
					defaultValue={field.initialValue}
					tabIndex={-1}
					className="sr-only"
					aria-label={`${label} form value`}
					aria-invalid={field.errors?.length ? true : undefined}
					aria-describedby={field.errors?.length ? field.errorId : undefined}
					onFocus={() => buttonRef.current?.focus()}
				/>
				<button
					ref={buttonRef}
					id={field.id}
					type="button"
					role="combobox"
					aria-activedescendant={isOpen ? activeOptionId : undefined}
					aria-controls={isOpen ? listboxId : undefined}
					aria-describedby={field.errors?.length ? field.errorId : undefined}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-invalid={field.errors?.length ? true : undefined}
					aria-label={`${label}${field.required ? ' required' : ''}`}
					aria-required={field.required}
					onBlur={() => control.blur()}
					onFocus={() => control.focus()}
					onClick={() => {
						if (isOpen) {
							setIsOpen(false)
							return
						}
						const selectedIndex = options.findIndex(
							(option) => option === selectedValue,
						)
						openMenu(selectedIndex >= 0 ? selectedIndex : 0)
					}}
					onKeyDown={handleKeyDown}
					className={`absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent px-5 pb-3 pr-12 pt-9 text-left text-base outline-none transition-colors duration-150 md:px-6 md:pr-12 ${
						selectedValue ? 'text-black' : 'text-black/60'
					}`}
				>
					<span className="block truncate">{selectedLabel}</span>
				</button>
				<ChevronDownIcon className="pointer-events-none absolute right-5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/60 md:right-6" />
				{isOpen ? (
					<div
						id={listboxId}
						role="listbox"
						aria-label={label}
						className="absolute left-0 right-0 top-full z-30 max-h-[360px] overflow-y-auto border border-black/10 bg-[#fffffa] shadow-[0_18px_42px_-24px_rgba(0,0,0,0.35)]"
					>
						{options.map((option, index) => {
							const isActive = index === activeIndex
							const isSelected = option === selectedValue

							return (
								<button
									id={`${fieldId}-option-${index}`}
									key={option}
									type="button"
									role="option"
									aria-selected={isSelected}
									onClick={() => selectOption(option)}
									onMouseEnter={() => setActiveIndex(index)}
									className={`block w-full px-5 py-4 text-left text-base leading-none transition-colors duration-150 md:px-6 ${
										isActive || isSelected
											? 'bg-black/[0.06] text-black'
											: 'bg-[#fffffa] text-black/60 hover:bg-black/[0.06] hover:text-black'
									}`}
								>
									{option}
								</button>
							)
						})}
					</div>
				) : null}
			</div>
			<FieldErrors
				id={field.errorId}
				errors={field.errors}
				className="px-5 pb-3 text-xs font-medium text-[#EC1C23] md:px-6"
			/>
		</div>
	)
}

function FloatingTextarea({
	label,
	field,
}: {
	label: string
	field: FieldMetadata<string, SalesLead>
}) {
	return (
		<div className="border-t border-black/10">
			<div className="group relative min-h-[140px]">
				<span className="pointer-events-none absolute left-0 top-0 h-full w-[2px] bg-black opacity-0 transition-opacity duration-150 group-focus-within:opacity-100" />
				<textarea
					{...getTextareaProps(field)}
					rows={3}
					placeholder=" "
					autoCorrect="off"
					className="peer absolute inset-0 h-full w-full resize-none bg-transparent px-5 pb-4 pt-9 text-base text-black outline-none md:px-6"
				/>
				<label
					htmlFor={field.id}
					className="pointer-events-none absolute left-5 top-1/2 max-w-[calc(100%-2.5rem)] -translate-y-1/2 text-base font-normal text-black/60 transition-[font-size,top,transform] duration-200 ease-out peer-focus:top-4 peer-focus:translate-y-0 peer-focus:text-xs peer-[:not(:placeholder-shown)]:top-4 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs md:left-6 md:max-w-[calc(100%-3rem)]"
				>
					{label}
					{field.required ? <span className="text-[#EC1C23]"> *</span> : null}
				</label>
			</div>
			<FieldErrors
				id={field.errorId}
				errors={field.errors}
				className="px-5 pb-3 text-xs font-medium text-[#EC1C23] md:px-6"
			/>
		</div>
	)
}

function FieldErrors({
	id,
	errors,
	className,
	role,
}: {
	id?: string
	errors?: Array<string>
	className?: string
	role?: 'alert'
}) {
	if (!errors?.length) return null

	return (
		<ul id={id} className={className} role={role}>
			{errors.map((error) => (
				<li key={error}>{error}</li>
			))}
		</ul>
	)
}

export function GridBackdrop() {
	return (
		<div className="pointer-events-none absolute inset-0 overflow-hidden">
			<div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(16,16,16,0.08)_1px,transparent_1px)] bg-[size:25%_100%]" />
			<div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-[#eef7f2] to-transparent opacity-80" />
		</div>
	)
}

function ArrowRightIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			aria-hidden="true"
		>
			<path d="M2 8h12M9 3l5 5-5 5" strokeLinecap="square" />
		</svg>
	)
}

function ChevronDownIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			aria-hidden="true"
		>
			<path d="M3 6l5 5 5-5" strokeLinecap="square" />
		</svg>
	)
}
